import { chunk } from '@/services/bulk-actions/bulk-actions.utils'
import { prKey, prTarget } from './my-work.utils'
import type { MyPr, MyWork, PrRefreshResponse } from './my-work.types'

/** Replaces the row with the same key, or adds it at the end when the list does not have it yet. */
function putRow(rows: MyPr[], pr: MyPr): MyPr[] {
  const key = prKey(pr)
  return rows.some(row => prKey(row) === key)
    ? rows.map(row => (prKey(row) === key ? pr : row))
    : [...rows, pr]
}

function dropRow(rows: MyPr[], key: string): MyPr[] {
  return rows.filter(row => prKey(row) !== key)
}

/**
 * The page with one PR read again: it moves to the section the fleet now puts it in, leaves the
 * sections it no longer belongs to (a merged PR leaves both PR lists), and every ticket that lists
 * it shows its new state. Every other row is left exactly as it was.
 */
export function withRefreshedPr(work: MyWork, refreshed: PrRefreshResponse): MyWork {
  const { pr, section } = refreshed
  const key = prKey(pr)
  return {
    ...work,
    myPrs: section === 'mine' ? putRow(work.myPrs, pr) : dropRow(work.myPrs, key),
    reviewRequested:
      section === 'review' ? putRow(work.reviewRequested, pr) : dropRow(work.reviewRequested, key),
    tickets: work.tickets.map(ticket =>
      ticket.prs.some(row => prKey(row) === key)
        ? { ...ticket, prs: ticket.prs.map(row => (prKey(row) === key ? pr : row)) }
        : ticket
    )
  }
}

/** The fleet reads at most this many folded rows per request. */
export const DETAILS_CHUNK = 20

/** The page with each folded row read put back in place under PR review, the only section folded rows come from. */
export function withPrDetails(work: MyWork, prs: MyPr[]): MyWork {
  return prs.reduce((page, pr) => withRefreshedPr(page, { pr, section: 'review', generatedAt: '' }), work)
}

/** The folded rows still to read, as `repo#id`, in chunks of DETAILS_CHUNK. */
export function deferredTargets(prs: MyPr[]): string[][] {
  return chunk(prs.filter(pr => pr.detailsDeferred).map(prTarget), DETAILS_CHUNK)
}
